import { Capacitor } from "@capacitor/core";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { capacitorHttpAdapter } from "./adapters/capacitorHttpAdapter";
import { API_CONFIG } from "./constants/api";

// Strip query string before logging a request path. era-web is patient-facing,
// so query params can carry user-entered values (search terms, etc.) that are
// PHI. Path segments may include the caller's own id, which is already on the
// Sentry scope as user.id, so the bare pathname is acceptable.
function logSafePath(url: string | undefined): string {
	if (!url) return "unknown";
	const queryIndex = url.indexOf("?");
	return queryIndex === -1 ? url : url.slice(0, queryIndex);
}

// Collapse high-cardinality path segments (numeric ids, UUIDs, long opaque
// hex tokens) to ":id" so Sentry Issue fingerprints group by ROUTE, not by the
// specific resource id — otherwise `/api/events/<id>` opens one Issue per id.
// Used only for grouping; the raw pathname is still logged in the event extra.
function fingerprintPath(pathname: string): string {
	return pathname
		.split("/")
		.map((seg) =>
			/^\d+$/.test(seg) ||
			/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
				seg,
			) ||
			/^[0-9a-f]{16,}$/i.test(seg)
				? ":id"
				: seg,
		)
		.join("/");
}

// Only use CapacitorHttp adapter on native platforms (ios/android)
// BUT: Force use of web adapter in development (for ngrok/zrok/local testing)
// This avoids Capacitor HTTP issues with self-signed certs and tunnels
const isNativePlatform = Capacitor.isNativePlatform();
const isDevelopment =
	import.meta.env.DEV ||
	import.meta.env.MODE === "development" ||
	API_CONFIG.BASE_URL.includes("localhost") ||
	API_CONFIG.BASE_URL.includes("ngrok") ||
	API_CONFIG.BASE_URL.includes("zrok");
const useNativeHttp = isNativePlatform && !isDevelopment;

console.log("🌐 API Configuration:", {
	isNativePlatform,
	isDevelopment,
	useNativeHttp: useNativeHttp ? "Capacitor HTTP" : "Web HTTP (axios)",
	baseURL: API_CONFIG.BASE_URL,
});

export const api = axios.create({
	baseURL: API_CONFIG.BASE_URL,
	withCredentials: true,
	timeout: API_CONFIG.TIMEOUT,
	headers: {
		...API_CONFIG.DEFAULT_HEADERS,
		// Skip ngrok browser warning in development
		...(isDevelopment ? { "ngrok-skip-browser-warning": "true" } : {}),
	},
	...(useNativeHttp ? { adapter: capacitorHttpAdapter } : {}),
});

// Store auth callbacks
// Handler can be async to properly await logout completion
let onUnauthorized: (() => void | Promise<void>) | null = null;
let getAccessToken: (() => Promise<string>) | null = null;

// Store global loading handler
let setGlobalLoading: ((loading: boolean, message?: string) => void) | null =
	null;

// Ref-count in-flight requests so the global loader stays shown across a burst
// of sequential/concurrent calls instead of flickering off-then-on between them
// (86ey31pzw — 2FA verify fires verifyOTP → complete2FA → getUserStatus back to
// back and the boolean toggling looked like two loaders). The loader only hides
// once the last request settles.
let pendingRequests = 0;
const incLoading = () => {
	pendingRequests += 1;
	if (pendingRequests === 1 && setGlobalLoading) {
		setGlobalLoading(true);
	}
};
const decLoading = () => {
	pendingRequests = Math.max(0, pendingRequests - 1);
	if (pendingRequests === 0 && setGlobalLoading) {
		setGlobalLoading(false);
	}
};

// A rejection from the request interceptor still flows through the response
// error interceptor in Axios, so mark such errors to avoid decrementing the
// ref-count twice (which would hide the loader while other requests are still
// in flight).
const LOADING_HANDLED = "__eraLoadingHandled";
const markLoadingHandled = (err: unknown): unknown => {
	if (err && typeof err === "object") {
		(err as Record<string, unknown>)[LOADING_HANDLED] = true;
	}
	return err;
};
const loadingAlreadyHandled = (err: unknown): boolean =>
	!!(
		err &&
		typeof err === "object" &&
		(err as Record<string, unknown>)[LOADING_HANDLED]
	);

export const setApiUnauthorizedHandler = (
	handler: () => void | Promise<void>,
) => {
	onUnauthorized = handler;
};

export const setApiTokenProvider = (tokenProvider: () => Promise<string>) => {
	getAccessToken = tokenProvider;
};

export const setApiLoadingHandler = (
	loadingHandler: (loading: boolean, message?: string) => void,
) => {
	setGlobalLoading = loadingHandler;
};

// Add request interceptor to attach token and show loading
api.interceptors.request.use(
	async (config) => {
		// Show global loading state (ref-counted)
		incLoading();

		// Check if this request should skip authentication
		// @ts-expect-error - skipAuth is a custom property we add to config
		const skipAuth = config.skipAuth === true;

		if (getAccessToken && !skipAuth) {
			try {
				const token = await getAccessToken();
				config.headers.Authorization = `Bearer ${token}`;
			} catch (error) {
				console.error("❌ [API] Failed to get access token");
				Sentry.logger.error("Access token acquisition failed", {
					feature: "api",
					event: "token_acquisition_failed",
					pathname: logSafePath(config.url),
					error_class:
						error instanceof Error ? error.constructor.name : "Unknown",
				});

				// If token refresh fails, redirect to login to prevent infinite loops
				if (onUnauthorized) {
					onUnauthorized();
				}

				// Hide loading and reject the request. incLoading() already ran
				// above, so decrement once here and mark the error so the response
				// interceptor (which this rejection still reaches) doesn't decrement
				// it a second time.
				decLoading();
				return Promise.reject(
					markLoadingHandled(
						new Error("Authentication failed - please log in again"),
					),
				);
			}
		}
		return config;
	},
	(error) => {
		console.error("❌ [API] Request error");
		Sentry.logger.error("API request setup failed", {
			feature: "api",
			event: "request_error",
			error_class: error instanceof Error ? error.constructor.name : "Unknown",
		});
		// incLoading() runs in the fulfilled handler, which did NOT run on this
		// pre-dispatch rejection path — so there is nothing to decrement here.
		// Mark it so the response interceptor doesn't decrement either.
		return Promise.reject(markLoadingHandled(error));
	},
);

// Add response interceptor for 401 handling and hide loading
api.interceptors.response.use(
	(response) => {
		// Hide global loading state on successful response (ref-counted)
		decLoading();
		return response;
	},
	(error) => {
		// Hide global loading state on error response (ref-counted). Skip if a
		// request-interceptor path already accounted for this error's loading
		// state, so the ref-count isn't decremented twice.
		if (!loadingAlreadyHandled(error)) {
			decLoading();
		}

		// Handle 401 Unauthorized errors
		if (error.response?.status === 401) {
			console.error("❌ [API] 401 Unauthorized - redirecting to login");
			Sentry.logger.warn("API request unauthorized", {
				feature: "api",
				event: "unauthorized_redirect",
				pathname: logSafePath(error.config?.url),
			});

			// Call the unauthorized handler if set (redirect to login)
			if (onUnauthorized) {
				onUnauthorized();
			}
		} else {
			// PHI-safe: status + code + pathname only. NOT error.message (vendor
			// diagnostics) and NOT the raw url (query string may carry PHI).
			console.error("❌ [API] Response error");
			const status = error.response?.status;
			const errorClass =
				typeof error.code === "string" ? error.code : "Unknown";
			const pathname = logSafePath(error.config?.url);
			Sentry.logger[status && status >= 500 ? "error" : "warn"](
				"API request failed",
				{
					feature: "api",
					event: "response_error",
					status: status ?? null,
					pathname,
					error_class: errorClass,
				},
			);

			// Server (5xx) and network failures also open a Sentry Issue — Logs
			// alone don't alert/group. 4xx stay Logs-only: they're expected/handled
			// (e.g. education-hub 404s while the content backend isn't deployed) and
			// would otherwise flood Issues. Synthetic error keeps it PHI-safe (no
			// error.message, no raw url); fingerprint groups by status + pathname.
			const isServerError = typeof status === "number" && status >= 500;
			const isNetworkError = !error.response && error.code !== "ERR_CANCELED";
			if (isServerError || isNetworkError) {
				const method = (error.config?.method ?? "unknown").toUpperCase();
				Sentry.captureException(
					new Error(`API ${status ?? "network"} error (${errorClass})`),
					{
						fingerprint: [
							"api-error",
							method,
							String(status ?? "network"),
							fingerprintPath(pathname),
						],
						tags: { feature: "api", api_status: String(status ?? "network") },
						extra: {
							pathname,
							method,
							error_class: errorClass,
							status: status ?? null,
						},
					},
				);
			}
		}

		return Promise.reject(error);
	},
);
